import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/authUtils';
import { Plus, RefreshCw, Trash2, Send, ArrowLeft, Copy, Check } from 'lucide-react';

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  margin-bottom: 1rem;

  &:hover {
    color: #374151;
    text-decoration: underline;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: bold;
`;

const Subtitle = styled.p`
  color: #6b7280;
  margin-bottom: 1.5rem;
`;

const Card = styled.div`
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const CardTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const InviteForm = styled.form`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Input = styled.input`
  flex: 1;
  min-width: 240px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const Button = styled.button`
  background-color: #3b82f6;
  color: white;
  font-weight: bold;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #2563eb;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RefreshButton = styled(Button)`
  background-color: #6b7280;

  &:hover {
    background-color: #4b5563;
  }
`;

const SmallButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  color: white;
  background-color: ${props => (props.danger ? '#ef4444' : '#3b82f6')};

  &:hover {
    background-color: ${props => (props.danger ? '#dc2626' : '#2563eb')};
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-size: 0.85rem;
`;

const Td = styled.td`
  border: 1px solid #e5e7eb;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => (props.active ? '#065f46' : '#92400e')};
  background-color: ${props => (props.active ? '#d1fae5' : '#fef3c7')};
`;

const EmptyState = styled.p`
  color: #6b7280;
  padding: 2rem 0;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  border: 1px solid #f87171;
  color: #b91c1c;
  padding: 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  background-color: #d1fae5;
  border: 1px solid #34d399;
  color: #065f46;
  padding: 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const WarningBox = styled.div`
  background-color: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
  padding: 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const LinkText = styled.code`
  flex: 1;
  background-color: white;
  border: 1px solid #fcd34d;
  border-radius: 0.25rem;
  padding: 0.4rem 0.6rem;
  font-size: 0.8rem;
  color: #78350f;
  overflow-x: auto;
  white-space: nowrap;
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background-color: #92400e;
  color: white;
  border: none;
  border-radius: 0.25rem;
  padding: 0.4rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #78350f;
  }
`;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Inviting an email that already has a speaker account is a normal case here
// (e.g. re-sending a lost invite, or inviting a returning speaker again) - it
// reuses the same account rather than rejecting, mirroring the backend.
const SpeakerManagement = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pendingInviteLink, setPendingInviteLink] = useState(null); // { email, link }
  const [linkCopied, setLinkCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = async () => {
    try {
      await api.get('/api/protected');
      setIsLoggedIn(true);
      fetchAccounts();
    } catch (err) {
      console.error('Error checking auth status:', err);
      navigate('/admin/login');
    }
  };

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/cms/accounts');
      const speakers = (response.data || [])
        .filter((account) => account.role === 'SPEAKER')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setAccounts(speakers);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load speakers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const extractErrorMessage = (err, fallback) => {
    const data = err.response?.data;
    return typeof data === 'string' && data ? data : fallback;
  };

  const sendInvite = async (email) => {
    setError('');
    setSuccessMessage('');
    setPendingInviteLink(null);
    setLinkCopied(false);
    try {
      const response = await api.post('/api/cms/accounts/speakers', { email });
      // The account/invitation are created either way - a failed email send (e.g. no
      // mail server in local dev) is a soft warning, not a reason to hide the result.
      // The backend only includes inviteToken when the email failed, specifically so
      // it can be shared manually as a fallback.
      if (response.data?.emailSent === false && response.data?.inviteToken) {
        const link = `${window.location.origin}/speaker/invite?token=${response.data.inviteToken}`;
        setPendingInviteLink({ email, link });
      } else {
        setSuccessMessage(`Invitation sent to ${email}.`);
      }
      await fetchAccounts();
      return true;
    } catch (err) {
      console.error('Error inviting speaker:', err);
      setError(extractErrorMessage(err, 'Failed to invite speaker. Please try again.'));
      return false;
    }
  };

  const handleCopyLink = async () => {
    if (!pendingInviteLink) return;
    try {
      await navigator.clipboard.writeText(pendingInviteLink.link);
      setLinkCopied(true);
    } catch (err) {
      console.error('Error copying link:', err);
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    const success = await sendInvite(email);
    setInviting(false);
    if (success) setInviteEmail('');
  };

  const handleReinvite = (email) => {
    sendInvite(email);
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Remove the speaker account for ${email}? This cannot be undone.`)) {
      return;
    }
    setError('');
    setSuccessMessage('');
    setPendingInviteLink(null);
    try {
      await api.delete(`/api/cms/accounts/speakers/${id}`);
      setAccounts((prev) => prev.filter((account) => account.id !== id));
      setSuccessMessage(`Removed speaker account for ${email}.`);
    } catch (err) {
      console.error('Error deleting speaker:', err);
      setError('Failed to remove speaker. Please try again.');
    }
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <Container>
      <BackLink to="/admin/dashboard">
        <ArrowLeft size={16} /> Back to Dashboard
      </BackLink>
      <Header>
        <Title>Speaker Management</Title>
        <RefreshButton onClick={fetchAccounts}>
          <RefreshCw size={18} /> Refresh
        </RefreshButton>
      </Header>
      <Subtitle>Invite speakers by email. Event-specific assignment isn't modeled yet.</Subtitle>

      <Card>
        <CardTitle>Invite a Speaker</CardTitle>
        <InviteForm onSubmit={handleInviteSubmit}>
          <Input
            type="email"
            required
            placeholder="speaker@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <Button type="submit" disabled={inviting}>
            <Plus size={18} /> {inviting ? 'Inviting...' : 'Invite Speaker'}
          </Button>
        </InviteForm>
      </Card>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
      {pendingInviteLink && (
        <WarningBox>
          Invited {pendingInviteLink.email}, but the invitation email failed to send
          (expected if email isn't configured locally). Share this link with them manually:
          <LinkRow>
            <LinkText>{pendingInviteLink.link}</LinkText>
            <CopyButton type="button" onClick={handleCopyLink}>
              {linkCopied ? <Check size={14} /> : <Copy size={14} />}
              {linkCopied ? 'Copied' : 'Copy'}
            </CopyButton>
          </LinkRow>
        </WarningBox>
      )}

      {loading ? (
        <p>Loading speakers...</p>
      ) : accounts.length === 0 ? (
        <EmptyState>No speakers invited yet.</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Email</Th>
              <Th>Status</Th>
              <Th>Invited</Th>
              <Th>Last Login</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <tr key={account.id}>
                <Td>{account.email}</Td>
                <Td>
                  <StatusBadge active={account.status === 'ACTIVE'}>
                    {account.status === 'ACTIVE' ? 'Active' : 'Pending Setup'}
                  </StatusBadge>
                </Td>
                <Td>{formatDate(account.createdAt)}</Td>
                <Td>{formatDate(account.lastLogin)}</Td>
                <Td>
                  <ActionGroup>
                    <SmallButton
                      type="button"
                      title="Send another invitation"
                      onClick={() => handleReinvite(account.email)}
                    >
                      <Send size={16} />
                    </SmallButton>
                    <SmallButton
                      type="button"
                      danger
                      title="Remove speaker account"
                      onClick={() => handleDelete(account.id, account.email)}
                    >
                      <Trash2 size={16} />
                    </SmallButton>
                  </ActionGroup>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default SpeakerManagement;
